import React, { useCallback, useMemo, useState } from 'react';
import GroupsRowList from './GroupsRowList';
import GroupsOrgWebView from './GroupsOrgWebView';
import GroupsFloatingAddBtn from './GroupsFloatingAddBtn';
import GroupsAddGroupEmptyState from './GroupsAddGroupEmptyState';
import {
  ownerCrownLabelsForOrganization,
  viewerCanRevealOrgOwnerBridge
} from '../../utils/groupsPage';
import {
  buildWebBrowseChildren,
  flattenOrgBrowseList,
  partitionOrgOwnerWebChildren,
  periodLabel,
  resolveGroupFromAllGroups,
  isDerivedGuardiansGroup
} from '../../utils/orgGroupTree';

/**
 * Organization groups panel — web map (folder drill-down or Show all) / list.
 */
const GroupsOrgGroupsPanel = ({
  organization,
  orgGpId = null,
  allGroups = [],
  browseFocus = null,
  focusGroupId = null,
  cameraNonce = 0,
  searchListSize = 0,
  loading = false,
  search = '',
  onSearch,
  searchPlaceholder = 'Search groups…',
  emptyMessage = 'No related groups found.',
  onSelectGroup,
  onSelectPeriod,
  canAddGroup = false,
  onAddGroup,
  platformOwnerViewer = false,
  userId = null,
  availableUgp = [],
  userGroupsPeriods = [],
  currentRole = null,
  showDerivedGuardiansNode = false,
  derivedParentGpIds = null
}) => {
  const [mode, setMode] = useState('web');
  // Off = previous folder drill-down; on = continuous hierarchy + camera focus.
  const [showAllHierarchy, setShowAllHierarchy] = useState(false);
  const showSearch = Boolean(onSearch) && searchListSize > 10;
  const organizationDisplayed =
    Boolean(organization?.id) && (showAllHierarchy || !browseFocus?.group);

  const derivedBrowseOptions = useMemo(
    () => ({
      derivedParentGpIds: showDerivedGuardiansNode ? derivedParentGpIds : null,
      includeDerivedGuardians: showDerivedGuardiansNode && !derivedParentGpIds,
      fullTree: showAllHierarchy
    }),
    [showDerivedGuardiansNode, derivedParentGpIds, showAllHierarchy]
  );

  const webTreeChildren = useMemo(
    () =>
      buildWebBrowseChildren(
        allGroups,
        organization?.id,
        orgGpId,
        showAllHierarchy ? null : browseFocus,
        derivedBrowseOptions
      ),
    [
      allGroups,
      organization?.id,
      orgGpId,
      browseFocus,
      showAllHierarchy,
      derivedBrowseOptions
    ]
  );

  const { ownerCornerNodes, mainTreeChildren } = useMemo(() => {
    if (!showAllHierarchy && browseFocus?.group) {
      return { ownerCornerNodes: [], mainTreeChildren: webTreeChildren };
    }
    return partitionOrgOwnerWebChildren(
      webTreeChildren,
      allGroups,
      organization?.id,
      orgGpId,
      organization
    );
  }, [
    webTreeChildren,
    allGroups,
    organization,
    orgGpId,
    browseFocus?.group,
    showAllHierarchy
  ]);

  const canRevealOrgOwner = useMemo(
    () =>
      viewerCanRevealOrgOwnerBridge({
        platformOwnerViewer,
        userId,
        availableUgp,
        userGroupsPeriods,
        currentRole,
        ownerCornerNodes,
        organizationDisplayed,
        organization
      }),
    [
      platformOwnerViewer,
      userId,
      availableUgp,
      userGroupsPeriods,
      currentRole,
      ownerCornerNodes,
      organizationDisplayed,
      organization
    ]
  );

  const ownerCrownLabels = useMemo(
    () => ownerCrownLabelsForOrganization(organization),
    [organization]
  );

  const openOrgOwnerTeam = useCallback(() => {
    const node = ownerCornerNodes[0];
    if (!node?.group) return;
    onSelectGroup?.(node);
  }, [ownerCornerNodes, onSelectGroup]);

  const focusedGroup =
    !showAllHierarchy && browseFocus?.group
      ? resolveGroupFromAllGroups(allGroups, browseFocus.group)
      : null;
  const webRoot = focusedGroup
    ? { ...focusedGroup, group_type: focusedGroup.group_type || 'team' }
    : organization;

  const listBrowseFocus = showAllHierarchy ? null : browseFocus;
  const listRows = useMemo(() => {
    const flat = flattenOrgBrowseList(
      allGroups,
      organization?.id,
      orgGpId,
      listBrowseFocus,
      [],
      0,
      organization,
      {
        derivedParentGpIds: showDerivedGuardiansNode ? derivedParentGpIds : null,
        includeDerivedGuardians: showDerivedGuardiansNode && !derivedParentGpIds
      }
    );
    const q = search.trim().toLowerCase();
    const filtered = q
      ? flat.filter((row) => {
          const name = (row.group?.name || '').toLowerCase();
          const type = (row.group?.group_type || '').toLowerCase();
          const pl = row.period ? periodLabel(row.period).toLowerCase() : '';
          return name.includes(q) || type.includes(q) || pl.includes(q);
        })
      : flat;

    const mapped = filtered.map((row) => {
      if (row.kind === 'period') {
        return {
          id: `period-${row.periodGpId}`,
          name: periodLabel(row.period),
          groupType: 'period',
          indentLevel: row.depth,
          metaParts: [row.group?.name || 'Group'],
          onClick: () =>
            onSelectPeriod?.({
              nodeType: 'period',
              group: row.group,
              period: row.period,
              periodGpId: row.periodGpId
            })
        };
      }
      if (row.isDerivedGuardians || isDerivedGuardiansGroup(row.group)) {
        return {
          id: `derived-guardians-${row.group?.id || row.derivedParentGpId || 'root'}`,
          name: row.group?.name || 'Guardians',
          groupType: 'derived',
          isDerivedRow: true,
          indentLevel: row.depth,
          metaParts: ['Person links'],
          onClick: () =>
            onSelectGroup?.({
              nodeType: 'group',
              group: row.group,
              isDerivedGuardians: true,
              derivedParentGpId: row.derivedParentGpId || row.group?.parent_groups_periods_id
            })
        };
      }
      return {
        id: `group-${row.group.id}-${row.depth}`,
        name: row.group.name,
        groupType: row.group.group_type || 'team',
        displayLabel: row.group.display_label || null,
        indentLevel: row.depth,
        metaParts: [
          row.group.periods?.length
            ? `${row.group.periods.length} period${row.group.periods.length === 1 ? '' : 's'}`
            : 'No periods',
          ...(row.group.membership_kind === 'anchor' ? ['Anchor'] : [])
        ],
        onClick: () => onSelectGroup?.({ nodeType: 'group', group: row.group })
      };
    });

    const showCrownRow =
      canRevealOrgOwner && ownerCornerNodes.length > 0 && organizationDisplayed;
    if (!showCrownRow) {
      return mapped;
    }

    const ownerGroup = ownerCornerNodes[0]?.group;
    if (!ownerGroup) {
      return mapped;
    }

    const crownRow = {
      id: `owner-crown-${ownerGroup.id}`,
      name: ownerGroup.name,
      groupType: ownerGroup.group_type || 'team',
      indentLevel: 0,
      isOwnerCrownRow: true,
      metaParts: [ownerCrownLabels.listLabel],
      onClick: openOrgOwnerTeam
    };

    if (!q) {
      return [crownRow, ...mapped];
    }

    const ownerHaystack = [
      ownerGroup.name,
      ownerGroup.group_type,
      ownerCrownLabels.listLabel
    ]
      .join(' ')
      .toLowerCase();
    if (ownerHaystack.includes(q)) {
      return [crownRow, ...mapped];
    }
    return mapped;
  }, [
    allGroups,
    organization,
    orgGpId,
    listBrowseFocus,
    search,
    showDerivedGuardiansNode,
    derivedParentGpIds,
    onSelectGroup,
    onSelectPeriod,
    canRevealOrgOwner,
    ownerCornerNodes,
    organizationDisplayed,
    ownerCrownLabels.listLabel,
    openOrgOwnerTeam
  ]);

  // options: { addMode, parentAttachGpId, anchorGroup, parentGroupName }
  const openAddGroup = (options = {}) => {
    if (!onAddGroup) return;
    const {
      addMode = null,
      parentAttachGpId = null,
      anchorGroup = null,
      parentGroupName = null
    } = options;
    if (anchorGroup) {
      onAddGroup({
        anchorGroup,
        parentGroupName: parentGroupName || anchorGroup.name,
        ...(addMode ? { addMode } : {}),
        ...(parentAttachGpId ? { parentAttachGpId } : {})
      });
      return;
    }
    if (!showAllHierarchy && browseFocus?.group) {
      onAddGroup({
        anchorGroup: browseFocus.group,
        parentGroupName: browseFocus.group.name,
        ...(addMode ? { addMode } : {}),
        ...(parentAttachGpId ? { parentAttachGpId } : {})
      });
      return;
    }
    onAddGroup({
      parentGpId: orgGpId,
      parentGroupName: organization?.name
    });
  };

  const showAddGroupEmpty =
    !loading &&
    (showAllHierarchy || !browseFocus?.group) &&
    canAddGroup &&
    onAddGroup &&
    listRows.length === 0 &&
    !search.trim();

  const viewTabs = (
    <div className="groups-view-tabs" role="tablist" aria-label="Groups view">
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'web'}
        className={`groups-view-tab${mode === 'web' ? ' is-active' : ''}`}
        onClick={() => setMode('web')}
      >
        Web <span className="groups-view-tab-count">{allGroups.length}</span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'list'}
        className={`groups-view-tab${mode === 'list' ? ' is-active' : ''}`}
        onClick={() => setMode('list')}
      >
        List
      </button>
    </div>
  );

  const viewHint = (
    <p className="groups-dms-hint groups-dms-hint--footer">
      {mode === 'web'
        ? showAllHierarchy
          ? 'Show all on: click a team to zoom in; crumbs zoom out. Click a period for members.'
          : 'Click a group for its periods; click a period for members. Turn on Show all for the full map.'
        : browseFocus?.group
          ? 'Use + to add under this team. Click a period for members.'
          : 'Indented by nesting. Click a period for members; click a group to browse its periods.'}
    </p>
  );

  const footerNav = (
    <div className="groups-org-web-footer-nav">
      {viewTabs}
      {viewHint}
      {loading && <span className="groups-org-groups-loading">loading…</span>}
    </div>
  );

  const listFooterTools =
    mode === 'list' && showSearch ? (
      <input
        type="search"
        className="groups-search groups-search--compact"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder={searchPlaceholder}
      />
    ) : null;

  const panelFooter = (
    <div className="groups-org-web-footer">
      <div className="groups-org-web-footer-slot">{footerNav}</div>
      <div className="groups-org-web-toolbar">{listFooterTools}</div>
    </div>
  );

  return (
    <section
      className={`groups-dms-section groups-org-groups-panel${
        mode === 'web' ? ' groups-org-groups-panel--web' : ' groups-org-groups-panel--list'
      }`}
    >
      {mode === 'web' ? (
        <GroupsOrgWebView
          organization={webRoot}
          orgGpId={orgGpId}
          treeChildren={mainTreeChildren}
          showOrgOwnerCrown={
            canRevealOrgOwner && ownerCornerNodes.length > 0 && organizationDisplayed
          }
          onOpenOrgOwner={openOrgOwnerTeam}
          ownerCrownAriaLabel={ownerCrownLabels.ariaLabel}
          ownerCrownTitle={ownerCrownLabels.title}
          isBrowseRoot={Boolean(!showAllHierarchy && browseFocus?.group)}
          showAllHierarchy={showAllHierarchy}
          onShowAllHierarchyChange={setShowAllHierarchy}
          loading={loading}
          onSelectGroup={onSelectGroup}
          onSelectPeriod={onSelectPeriod}
          footerSlot={footerNav}
          canAddGroup={canAddGroup}
          onAddGroup={openAddGroup}
          focusGroupId={showAllHierarchy ? focusGroupId : null}
          cameraNonce={cameraNonce}
        />
      ) : (
        <div className="groups-org-list-view">
          <div className="groups-org-list-body">
            <div
              className={`groups-org-list-scroll${
                showAddGroupEmpty ? ' groups-org-list-scroll--empty' : ''
              }`}
            >
              <GroupsRowList
                embedded
                loading={loading}
                emptyMessage={search.trim() ? 'No matching groups.' : emptyMessage}
                emptyState={
                  showAddGroupEmpty ? (
                    <GroupsAddGroupEmptyState onClick={() => openAddGroup()} />
                  ) : null
                }
                rows={listRows}
                listVariant="thread"
              />
            </div>
            {canAddGroup && onAddGroup && !showAddGroupEmpty ? (
              <GroupsFloatingAddBtn
                className="groups-org-floating-add-btn--list"
                onClick={() => openAddGroup()}
                ariaLabel={browseFocus?.group ? 'Add nested team' : 'Add group'}
                title={browseFocus?.group ? 'Add nested team' : 'Add group'}
              />
            ) : null}
          </div>
          {panelFooter}
        </div>
      )}
    </section>
  );
};

export default GroupsOrgGroupsPanel;
